import React from "react";

/**
 * Button - Atomic component for all buttons in the application
 *
 * @param {Object} props
 * @param {string} props.variant - primary, secondary, ghost
 * @param {function} props.onClick - click handler
 * @param {boolean} props.disabled - is button disabled
 * @param {boolean} props.isLoading - show loading state
 * @param {React.ReactNode} props.children - button content
 * @param {string} props.className - additional classes
 */
const Button = ({
  variant = "primary",
  onClick,
  disabled = false,
  isLoading = false,
  children,
  className = "",
  ...rest
}) => {
  // Define base styles based on variant
  const baseStyles = {
    primary:
      "bg-[#4db1e0] text-white hover:bg-[#3da0cf] transition-colors duration-200",
    secondary:
      "border border-[#4db1e0] text-[#4db1e0] hover:bg-[#262b30] hover:border-[#3da0cf] hover:text-[#3da0cf] transition-all duration-200",
    ghost:
      "text-gray-300 hover:text-white hover:bg-[#262b30]/50 transition-all duration-200",
  };

  // Loading indicator
  const loadingIndicator = (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`px-4 py-2 rounded-md transition flex items-center justify-center ${baseStyles[variant]} ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      {...rest}
    >
      {isLoading && loadingIndicator}
      {children}
    </button>
  );
};

export default Button;
